/**
 * Beta Signup — public signup page for the Bloq Beta tester program.
 *
 * Same layout DNA as /invest and /fund-a-feature:
 *   - MarketingNav + MarketingFooter
 *   - bloq-section / bloq-container + bloq-fade-up heading
 *   - 3-up stat cards explaining what beta access involves
 *   - bloq-glass-strong form card holding the Cognito embed
 *
 * The Cognito HTML lives in /api/marketing/settings under the
 * `beta_signup_embed` key. Admins paste/update it from Settings →
 * Marketing → Form embeds without a deploy.
 *
 * Fallback: when no embed is configured yet, the form card swaps in a
 * dashed-border "Form coming soon" box with a /contact CTA — same
 * pattern as /invest. Never breaks on missing config.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BadgeCheck, MessageCircleHeart, FlaskConical } from "lucide-react";
import MarketingNav from "@/components/MarketingNav";
import MarketingFooter from "@/components/MarketingFooter";
import HtmlEmbed from "@/components/HtmlEmbed";
import api from "@/lib/api";

export default function BetaSignup() {
  const [embed, setEmbed] = useState("");
  useEffect(() => {
    api.get("/marketing/settings")
      .then((r) => setEmbed(r.data?.beta_signup_embed || ""))
      .catch(() => setEmbed(""));
  }, []);

  const perks = [
    {
      icon: BadgeCheck,
      t: "Early access",
      d: "Use Bloq while it's actively being built — features land for testers first, weeks before they go GA.",
    },
    {
      icon: MessageCircleHeart,
      t: "Direct line to the team",
      d: "Tell us what works, what doesn't, and what you wish existed. Your feedback genuinely shapes the roadmap.",
    },
    {
      icon: FlaskConical,
      t: "Help shape what ships",
      d: "Vote on upcoming features, try experimental flows, and influence the workspace before everyone else has it.",
    },
  ];

  return (
    <div className="bg-[#fbfcfc] min-h-screen">
      <MarketingNav />

      <section className="bloq-section">
        <div className="bloq-container">
          <div className="max-w-3xl mb-14 bloq-fade-up">
            <p className="bloq-eyebrow">Bloq Beta Program</p>
            <h1 className="bloq-display text-5xl sm:text-6xl mt-3 mb-5">
              Be among the first <span className="bloq-gradient-text">to use Bloq.</span>
            </h1>
            <p className="text-lg text-[#4a4a52] leading-relaxed">
              We&rsquo;re inviting a small group of solopreneurs, consultants, and small teams to try Bloq
              ahead of public launch. You&rsquo;ll get early access to the entire workspace, a direct line to
              our team, and the chance to influence what we build next.
            </p>
          </div>

          {/* 3-up perks grid — same layout language as /invest's TAM/ICP/Wedge cards */}
          <div className="grid sm:grid-cols-3 gap-5 mb-16">
            {perks.map((p, i) => {
              const I = p.icon;
              return (
                <div key={i} className="bloq-card p-7" data-testid={`beta-perk-${i}`}>
                  <div
                    className="w-11 h-11 rounded-lg flex items-center justify-center mb-4"
                    style={{ background: "rgba(0,94,122,0.1)", color: "#005e7a" }}
                  >
                    <I className="w-5 h-5" />
                  </div>
                  <p className="bloq-headline text-xl mb-2 text-[#005e7a]">{p.t}</p>
                  <p className="text-sm text-[#4a4a52] leading-relaxed">{p.d}</p>
                </div>
              );
            })}
          </div>

          {/* Signup form — Cognito embed slot. Falls back to a contact-form CTA
              when nothing's pasted yet. Identical structure to /invest. */}
          <div className="bloq-glass-strong p-10 rounded-2xl" data-testid="beta-form-card">
            <p className="bloq-eyebrow">Apply for the beta</p>
            <h2 className="bloq-headline text-3xl mt-3 max-w-xl mb-4">
              Tell us a bit about yourself and we&rsquo;ll send you an invite.
            </h2>
            <p className="text-[#4a4a52] mb-8 max-w-xl">
              We review every application personally and reach out within 3 business days with next
              steps. Spots are limited so we can give every tester a great first impression.
            </p>
            {embed ? (
              <HtmlEmbed
                html={embed}
                className="bloq-cognito-embed"
                testId="beta-cognito-embed"
              />
            ) : (
              <div className="border-2 border-dashed border-[#005e7a]/30 rounded-xl p-8 text-center bg-white/50">
                <p className="text-sm text-[#82828c] mb-4">
                  Beta signup form coming soon — we&rsquo;re finalizing the application flow.
                </p>
                <Link
                  to="/contact?topic=beta-signup"
                  className="bloq-btn-primary inline-flex items-center gap-2"
                  data-testid="beta-fallback-cta"
                >
                  Email us in the meantime <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            )}
          </div>

          <p className="text-xs text-[#82828c] text-center mt-10">
            Bloq is currently in beta — features may change as we iterate.{" "}
            <Link to="/beta-disclaimer" className="text-[#005e7a] underline" data-testid="beta-disclaimer-link">
              Read the beta release notes
            </Link>.
          </p>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
